import React, {useState} from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {CouponState} from 'app/components/BillingManager/hooks/useCoupon';
import {Notifications} from 'app/components/Notifications';
import {ValidateCoupon} from 'app/components/features/billing/coupons';
import {CouponDetails, CouponIcon} from 'app/components/entities/billing/coupons';
import {formatCouponError} from 'app/components/entities/billing/base';
import {StyleSx} from 'app/types/common';
import {Payment} from 'app/components/entities/billing/payment';

const textSx: StyleSx = {textWrap: 'balance', mt: 1, textAlign: 'center'};

interface Props {
  state: CouponState;
  hasConnect: boolean;
  method: Payment.PaymentMethod;
  onChange: (value: string) => void;
  onValidate: (value: string) => Promise<void>;
  onActivate: () => Promise<void>;
  onClose: () => void;
}

export function CouponForm({
  state,
  hasConnect,
  method,
  onChange,
  onValidate,
  onActivate,
  onClose,
}: Props) {
  const [activating, setActivating] = useState(false);

  const isCardPayment = method === 'credit_card';
  const isPendingInvoicePayment = method === 'invoice_pending';

  const isValidCoupon = state.type === 'valid';

  const isInvoiceCoupon = isValidCoupon && state.instance.is_ibb === true;

  const canActivate = isValidCoupon && (isInvoiceCoupon || !isPendingInvoicePayment);

  const forceInvoicePayment = canActivate && isInvoiceCoupon && isCardPayment;

  const handleActivate = async () => {
    try {
      setActivating(true);
      await onActivate();
    } catch {
      Notifications.addErrorNotification('Could not activate coupon');
    } finally {
      setActivating(false);
    }
  };

  return (
    <>
      <DialogTitle variant="h5">Activate voucher</DialogTitle>

      <DialogContent>
        <Stack alignItems="center">
          <Typography sx={{mb: 3, textAlign: 'center'}}>
            You will get credits on your account that you can spend <br />
            on the premium plan, connect subscription or for using livescrypt devices.
          </Typography>

          <ValidateCoupon
            state={state}
            autoFocus={true}
            onChange={onChange}
            onValidate={onValidate}
          />

          <Box my={2} minHeight={34}>
            {state.type === 'typing' && (
              <Typography data-id="coupon-note" color="error.main">
                To apply voucher it must be validated
              </Typography>
            )}

            {state.type === 'invalid' && (
              <Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
                <CouponIcon valid={false} />
                <Typography data-id="coupon-error" color="error.main">
                  {formatCouponError(state.error)}
                </Typography>
              </Stack>
            )}

            {state.type === 'valid' && (
              <>
                <Stack direction="row" justifyContent="center" alignItems="center" gap={1}>
                  <CouponIcon valid={true} />
                  <CouponDetails coupon={state.instance} />
                </Stack>

                {state.instance.product === 'connect' && !hasConnect && (
                  <Typography data-id="coupon-note" sx={textSx}>
                    By activating this Epiphan Connect coupon you will be subscribed to Epiphan
                    Connect Pro plan
                  </Typography>
                )}

                {forceInvoicePayment && (
                  <Typography sx={textSx} data-id="coupon-note">
                    On activation of this voucher, your account will be switched to invoice-based
                    billing
                  </Typography>
                )}

                {!isInvoiceCoupon && isPendingInvoicePayment && (
                  <Typography sx={textSx} color="error.main" data-id="coupon-note">
                    You will be able to activate this voucher when your request for invoice-based
                    billing is approved.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          data-id="activate_button"
          variant="contained"
          color="success"
          loading={activating}
          disabled={!canActivate}
          onClick={handleActivate}
        >
          Activate
        </LoadingButton>

        <Button data-id="cancel-button" variant="outlined" color="info" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </>
  );
}
